// StudyDost — History Page (Professional Redesign)
// frontend/src/pages/HistoryPage.js

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Smartphone, Globe, Search, Filter } from 'lucide-react';
import { questionAPI } from '../services/api';

const SUBJECT_CONFIG = {
  MATH:          { emoji: '🧮', color: '#4F46E5', bg: '#EEF2FF', label: 'Math' },
  PHYSICS:       { emoji: '⚡', color: '#0891B2', bg: '#E0F7FA', label: 'Physics' },
  CHEMISTRY:     { emoji: '🧪', color: '#D97706', bg: '#FEF3C7', label: 'Chemistry' },
  BIOLOGY:       { emoji: '🌿', color: '#059669', bg: '#D1FAE5', label: 'Biology' },
  SCIENCE:       { emoji: '🔬', color: '#7C3AED', bg: '#EDE9FE', label: 'Science' },
  ENGLISH:       { emoji: '📖', color: '#BE185D', bg: '#FCE7F3', label: 'English' },
  HINDI:         { emoji: '🔤', color: '#C2410C', bg: '#FFEDD5', label: 'Hindi' },
  HISTORY:       { emoji: '📜', color: '#92400E', bg: '#FEF3C7', label: 'History' },
  GEOGRAPHY:     { emoji: '🗺️', color: '#065F46', bg: '#D1FAE5', label: 'Geography' },
  CIVICS:        { emoji: '🏛️', color: '#1D4ED8', bg: '#DBEAFE', label: 'Civics' },
  ECONOMICS:     { emoji: '📊', color: '#9F1239', bg: '#FFE4E6', label: 'Economics' },
  SOCIAL_SCIENCE:{ emoji: '🌍', color: '#854D0E', bg: '#FEF9C3', label: 'SST' },
  COMPUTER:      { emoji: '💻', color: '#5B21B6', bg: '#EDE9FE', label: 'Computer' },
  EVS:           { emoji: '🌱', color: '#166534', bg: '#DCFCE7', label: 'EVS' },
  OTHER:         { emoji: '📝', color: '#475569', bg: '#F1F5F9', label: 'General' },
};

function getSubject(s) {
  return SUBJECT_CONFIG[s] || SUBJECT_CONFIG.OTHER;
}

function cleanAnswerText(text) {
  if (!text) return '';
  return text
    // Strip WhatsApp bold: *text* → text (but keep **text** for markdown bold)
    .replace(/(?<!\*)\*(?!\*)([^*\n]+)(?<!\*)\*(?!\*)/g, '$1')
    // Strip emoji step markers added by formatter (✅ *, 🔍 Step etc stay readable)
    // Normalize multiple blank lines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function renderAnswer(text) {
  if (!text) return null;
  const cleaned = cleanAnswerText(text);
  return cleaned.split('\n').map((line, i, arr) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const rendered = parts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**'))
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      return part;
    });
    return (
      <React.Fragment key={i}>
        {rendered}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

function QuestionCard({ q, index }) {
  const [expanded, setExpanded] = useState(false);
  const sub = getSubject(q.subject);
  const isLong = q.answerText && q.answerText.length > 220;
  const date = new Date(q.createdAt);
  const dateStr = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      ...styles.card,
      animationDelay: `${index * 0.05}s`,
    }}>
      {/* Card header row */}
      <div style={styles.cardHeader}>
        <div style={{ ...styles.subjectPill, background: sub.bg, color: sub.color }}>
          {sub.emoji} {sub.label}
        </div>
        <div style={styles.cardMeta}>
          {q.source === 'WHATSAPP'
            ? <Smartphone size={11} color="#25D366" />
            : <Globe size={11} color="#4F46E5" />
          }
          <span style={styles.metaDate}>{dateStr} · {timeStr}</span>
          {q.modelUsed === 'cache' && (
            <span style={styles.cacheTag}>⚡ Instant</span>
          )}
        </div>
      </div>

      {/* Question */}
      <div style={styles.questionRow}>
        <div style={styles.questionQ}>Q</div>
        <p style={styles.questionText}>
          {q.questionText?.startsWith('📷') || q.source === 'WEB_IMAGE'
            ? <><span style={{marginRight:6}}>📷</span><em style={{color:'#6B7280'}}>{q.questionText?.replace('📷 ','') || 'Image question'}</em></>
            : q.questionText
          }
        </p>
      </div>

      {/* Answer */}
      {q.answerText && (
        <div style={styles.answerBlock}>
          <div style={{
            ...styles.answerContent,
            maxHeight: expanded ? 'none' : 110,
            overflow: expanded ? 'visible' : 'hidden',
            WebkitMaskImage: !expanded && isLong
              ? 'linear-gradient(to bottom, black 55%, transparent 100%)'
              : 'none',
            maskImage: !expanded && isLong
              ? 'linear-gradient(to bottom, black 55%, transparent 100%)'
              : 'none',
          }}>
            {renderAnswer(q.answerText)}
          </div>
          {isLong && (
            <button style={styles.expandBtn} onClick={() => setExpanded(!expanded)}>
              {expanded
                ? <><ChevronUp size={14} /> Less dikhao</>
                : <><ChevronDown size={14} /> Poora jawab padho</>
              }
            </button>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={styles.cardFooter}>
        <span style={{
          ...styles.tierTag,
          background: q.tier === 'FREE' ? '#F1F5F9' : q.tier === 'SUBSCRIPTION' ? '#EEF2FF' : '#FEF3C7',
          color: q.tier === 'FREE' ? '#64748B' : q.tier === 'SUBSCRIPTION' ? '#4F46E5' : '#D97706',
        }}>
          {q.tier === 'FREE' ? 'Free' : q.tier === 'SUBSCRIPTION' ? '⭐ Subscriber' : '₹5'}
        </span>
      </div>
    </div>
  );
}

export default function HistoryPage() {
  const [search, setSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('ALL');

  const { data, isLoading } = useQuery({
    queryKey: ['history'],
    queryFn: () => questionAPI.getHistory().then(r => r.data),
  });

  const questions = data?.questions || [];

  // Subject counts for filter bar
  const subjectCounts = questions.reduce((acc, q) => {
    const s = q.subject || 'OTHER';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const topSubjects = Object.entries(subjectCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Filter questions
  const filtered = questions.filter(q => {
    const matchSearch = !search ||
      q.questionText?.toLowerCase().includes(search.toLowerCase()) ||
      q.answerText?.toLowerCase().includes(search.toLowerCase());
    const matchSubject = filterSubject === 'ALL' || (q.subject || 'OTHER') === filterSubject;
    return matchSearch && matchSubject;
  });

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerBg} />
        <div style={styles.headerContent}>
          <h1 style={styles.headerTitle}>📋 Question History</h1>
          <p style={styles.headerSub}>Aapke saare sawaal aur jawab</p>

          {/* Stats row */}
          {questions.length > 0 && (
            <div style={styles.statsRow}>
              <div style={styles.statBox}>
                <div style={styles.statNum}>{questions.length}</div>
                <div style={styles.statLbl}>Total</div>
              </div>
              {topSubjects.slice(0, 3).map(([sub, count]) => {
                const s = getSubject(sub);
                return (
                  <div key={sub} style={styles.statBox}>
                    <div style={styles.statNum}>{count}</div>
                    <div style={styles.statLbl}>{s.emoji} {s.label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Search + filter */}
      <div style={styles.controls}>
        <div style={styles.searchBox}>
          <Search size={16} color="#94A3B8" />
          <input
            style={styles.searchInput}
            placeholder="Sawaal ya jawab dhundho..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={styles.filterRow}>
          <Filter size={13} color="#94A3B8" />
          <button
            style={{ ...styles.filterChip, background: filterSubject === 'ALL' ? '#4F46E5' : '#F1F5F9', color: filterSubject === 'ALL' ? '#FFFFFF' : '#64748B' }}
            onClick={() => setFilterSubject('ALL')}
          >
            All
          </button>
          {topSubjects.map(([sub]) => {
            const s = getSubject(sub);
            const active = filterSubject === sub;
            return (
              <button
                key={sub}
                style={{ ...styles.filterChip, background: active ? s.color : s.bg, color: active ? '#FFFFFF' : s.color }}
                onClick={() => setFilterSubject(active ? 'ALL' : sub)}
              >
                {s.emoji} {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {isLoading && (
          <div style={styles.center}>
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Load ho raha hai...</p>
          </div>
        )}

        {!isLoading && questions.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyEmoji}>📭</div>
            <h3 style={styles.emptyTitle}>Abhi koi sawaal nahi</h3>
            <p style={styles.emptyDesc}>Chat mein jaao aur pehla sawaal poochho!</p>
          </div>
        )}

        {!isLoading && questions.length > 0 && filtered.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyEmoji}>🔍</div>
            <h3 style={styles.emptyTitle}>Kuch nahi mila</h3>
            <p style={styles.emptyDesc}>Alag search term try karein</p>
          </div>
        )}

        {filtered.map((q, i) => (
          <QuestionCard key={q.id} q={q} index={i} />
        ))}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  page: { height: '100%', overflowY: 'auto', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif" },

  // Header
  header: { position: 'relative', overflow: 'hidden', padding: '24px 20px 28px' },
  headerBg: { position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0F172A 0%, #1E3A5F 100%)' },
  headerContent: { position: 'relative', zIndex: 1 },
  headerTitle: { fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, color: '#FFFFFF', marginBottom: 4, letterSpacing: '-0.01em' },
  headerSub: { fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 20 },
  statsRow: { display: 'flex', gap: 10 },
  statBox: { background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', borderRadius: 12, padding: '10px 14px', minWidth: 60, border: '1px solid rgba(255,255,255,0.1)' },
  statNum: { fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, color: '#FFFFFF', lineHeight: 1 },
  statLbl: { fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 3, whiteSpace: 'nowrap' },

  // Controls
  controls: { padding: '14px 16px 4px', background: '#FFFFFF', borderBottom: '1px solid #F1F5F9' },
  searchBox: { display: 'flex', alignItems: 'center', gap: 10, background: '#F8FAFF', border: '1.5px solid #E2E8F0', borderRadius: 12, padding: '10px 14px', marginBottom: 10 },
  searchInput: { flex: 1, border: 'none', background: 'transparent', fontSize: 14, outline: 'none', fontFamily: "'DM Sans', sans-serif", color: '#0F172A' },
  filterRow: { display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', paddingBottom: 10 },
  filterChip: { flexShrink: 0, border: 'none', borderRadius: 100, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontFamily: "'DM Sans', sans-serif" },

  // Content
  content: { padding: '14px 16px 80px' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 12 },
  spinner: { width: 36, height: 36, border: '3px solid #E2E8F0', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },
  loadingText: { fontSize: 14, color: '#94A3B8' },
  empty: { textAlign: 'center', padding: '60px 20px' },
  emptyEmoji: { fontSize: 52, marginBottom: 14 },
  emptyTitle: { fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 700, color: '#0F172A', marginBottom: 8 },
  emptyDesc: { fontSize: 14, color: '#94A3B8' },

  // Card
  card: {
    background: '#FFFFFF',
    borderRadius: 18,
    padding: '16px',
    marginBottom: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    border: '1px solid #F1F5F9',
    animation: 'cardIn 0.4s ease both',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  subjectPill: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 100 },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 5 },
  metaDate: { fontSize: 11, color: '#94A3B8' },
  cacheTag: { fontSize: 10, fontWeight: 700, color: '#D97706', background: '#FEF3C7', padding: '2px 6px', borderRadius: 100 },

  // Question
  questionRow: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 },
  questionQ: { width: 24, height: 24, borderRadius: 8, background: '#4F46E5', color: '#FFFFFF', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "'Sora', sans-serif" },
  questionText: { fontSize: 14, fontWeight: 600, color: '#0F172A', lineHeight: 1.5, flex: 1 },

  // Answer
  answerBlock: { background: '#F8FAFF', borderRadius: 12, padding: '12px 14px', borderLeft: '3px solid #DBEAFE', marginBottom: 12 },
  answerContent: { fontSize: 13, color: '#374151', lineHeight: 1.75, transition: 'max-height 0.3s ease' },
  expandBtn: { display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#4F46E5', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginTop: 8, padding: '4px 0', fontFamily: "'DM Sans', sans-serif" },

  // Footer
  cardFooter: { display: 'flex', gap: 6 },
  tierTag: { fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 100 },
};
